import React, { useCallback, useEffect, useState } from 'react';
import { crm } from './crmApi';
import type { CrmAuth } from './crmApi';
import CrmAllFollowUps from './AllFollowUps';
import { CrmAttendanceManager, CrmAttendanceRequests, CrmMyAttendance } from './Attendance';
import { CrmCallFixes, CrmMyCallFixes } from './CallFixes';
import CrmDashboard from './Dashboard';
import CrmDeletedLeads from './DeletedLeads';
import { CrmFormsAgain } from './FormsAgain';
import { CrmHolidaysManager, HolidayList } from './Holidays';
import LeadDesk from './LeadDesk';
import { CrmLeadRequests, CrmRequestLead } from './LeadRequests';
import LeadPanel from './LeadPanel';
import { CrmAllLeads, CrmMoveHistory, CrmTeamToday } from './ManagerCrm';
import { CrmMeetings } from './Meetings';
import { EodDayView, LeadStatusView, MonitorNumbers } from './Monitor';
import MyWeek from './MyWeek';
import CrmRecordings from './Recordings';
import SalesOverview from './SalesOverview';
import { CrmFollowUps, CrmMyLeads, CrmToday } from './SalesCrm';
import { CrmMyShift, CrmShiftRequests, CrmShiftsManager } from './Shifts';
import CrmStageLeads from './StageLeads';
import { PageHead, Tabs, useCrmMe } from './ui';

// ── Pages with tabs ──────────────────────────────────────────
// One menu item each, with the pages that belong together as tabs inside it,
// so the menu stays short (CrmApp.tsx, session.ts). The tab is in the address
// (?tab=), so a notification or a button can open the right one.

export interface HubProps { auth: CrmAuth; tab?: string; onTab: (tab: string) => void; onNavigate: (page: string, stage?: string) => void }
type TabDef = { id: string; label: string; badge?: number; render: () => React.ReactNode };

const Hub: React.FC<{ title: string; sub?: string; tabs: TabDef[]; tab?: string; onTab: (t: string) => void; label: string }> = ({ title, sub, tabs, tab, onTab, label }) => {
  const current = tabs.find(t => t.id === tab) || tabs[0];
  return (
    <div className="space-y-4">
      <PageHead title={title} sub={sub} />
      <Tabs label={label} value={current.id} onChange={onTab} tabs={tabs.map(t => ({ id: t.id, label: t.label, badge: t.badge }))} />
      <div key={current.id}>{current.render()}</div>
    </div>
  );
};

// Managers: every lead, the team's follow-ups, the poll stages, the closed
// ones, Junk Leads (nobody picked up after everyone's tries; the waiting
// list until 28 Oct 2026) and the deleted ones
const LEADS_TAB: Record<string, string> = { waiting: 'junk' };
export const ManagerLeads: React.FC<HubProps> = ({ auth, tab, onTab }) => (
  <Hub title="Leads" sub="Every lead in the team. Pick a view; open a lead to call, move, edit or delete it." label="Which leads" tab={tab && (LEADS_TAB[tab] || tab)} onTab={onTab} tabs={[
    { id: 'all', label: 'All leads', render: () => <CrmAllLeads auth={auth} embedded /> },
    { id: 'forms', label: 'Forms again', render: () => <CrmFormsAgain auth={auth} embedded /> },
    { id: 'followups', label: 'Follow-ups', render: () => <CrmAllFollowUps auth={auth} embedded /> },
    { id: 'poll', label: 'Poll · Interested', render: () => <CrmStageLeads auth={auth} stages={['Poll Sent', 'Interested', 'No Response']} isManager embedded /> },
    { id: 'closed', label: 'Cold · Not interested', render: () => <CrmStageLeads auth={auth} stages={['Cold Lead', 'Not Interested']} isManager embedded /> },
    { id: 'junk', label: 'Junk Leads', render: () => <CrmStageLeads auth={auth} stages={['Junk Lead']} isManager embedded /> },
    { id: 'deleted', label: 'Deleted', render: () => <CrmDeletedLeads auth={auth} embedded /> },
  ]} />
);

// isAdmin: the admin sets the offices and attendance rules
export const ManagerTeam: React.FC<HubProps & { isAdmin?: boolean }> = ({ auth, tab, onTab, isAdmin = false }) => (
  <Hub title="Team & Shifts" sub="Who is working today, who started work and when, who is on which shift, and the days the office is closed" label="Team" tab={tab} onTab={onTab} tabs={[
    { id: 'today', label: 'Team today', render: () => <CrmTeamToday auth={auth} embedded onHolidays={() => onTab('holidays')} /> },
    { id: 'attendance', label: 'Attendance', render: () => <CrmAttendanceManager auth={auth} isAdmin={isAdmin} /> },
    { id: 'shifts', label: 'Shifts', render: () => <CrmShiftsManager auth={auth} /> },
    { id: 'holidays', label: 'Holidays', render: () => <CrmHolidaysManager auth={auth} /> },
  ]} />
);

// Lead requests, shift change requests, call fixes and attendance times, with how many are waiting
export const ManagerRequests: React.FC<HubProps & { onChanged?: () => void }> = ({ auth, tab, onTab, onChanged }) => {
  const [counts, setCounts] = useState<{ leads: number; shifts: number; fixes: number; attendance: number } | null>(null);
  const load = useCallback(async () => {
    try {
      const [l, s, f, a] = await Promise.all([crm.leadRequests(auth, 'Pending'), crm.shiftRequests(auth, 'Pending'), crm.callFixes(auth, 'Pending'),
        crm.attRequests(auth, 'Pending').catch(() => null)]);
      setCounts({ leads: l.counts.Pending, shifts: s.counts.Pending, fixes: f.counts.Pending, attendance: a?.counts?.Pending || 0 });
    } catch { /* the tabs show their own errors */ }
  }, [auth]);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged?.(); };
  return (
    <Hub title="Requests" sub="Leads salespeople added themselves, shift changes they asked for, calls they logged wrongly, and attendance times to mark" label="Requests" tab={tab} onTab={onTab} tabs={[
      { id: 'leads', label: 'Lead requests', badge: counts?.leads, render: () => <CrmLeadRequests auth={auth} embedded onChanged={changed} /> },
      { id: 'shifts', label: 'Shift requests', badge: counts?.shifts, render: () => <CrmShiftRequests auth={auth} onChanged={changed} /> },
      { id: 'fixes', label: 'Call fixes', badge: counts?.fixes, render: () => <CrmCallFixes auth={auth} embedded onChanged={changed} /> },
      { id: 'attendance', label: 'Attendance', badge: counts?.attendance, render: () => <CrmAttendanceRequests auth={auth} onChanged={changed} /> },
    ]} />
  );
};

// The Monitor: what each salesperson did (calls, brochures, polls, poll
// answers, converted), where every lead is now, the day's EOD reports; then
// the charts, the other home (Sales Overview for a Lead Manager, the Lead
// Desk for a Sales Admin), recordings and the move history
const MONITOR_TAB: Record<string, string> = { dashboard: 'charts' };   // the old Reports tab
export const ManagerMonitor: React.FC<HubProps & { name: string; leadManager: boolean; self?: React.ComponentProps<typeof LeadDesk>['self'] }> = ({ auth, tab, onTab, onNavigate, name, leadManager, self }) => (
  <Hub title="Monitor" sub="What each salesperson did, where every lead is now, and the day's EOD reports" label="Monitor" tab={tab && (MONITOR_TAB[tab] || tab)} onTab={onTab} tabs={[
    { id: 'numbers', label: 'Team numbers', render: () => <MonitorNumbers auth={auth} onNavigate={onNavigate} /> },
    { id: 'status', label: 'Lead status', render: () => <LeadStatusView auth={auth} /> },
    { id: 'eod', label: 'Daily EOD', render: () => <EodDayView auth={auth} /> },
    { id: 'charts', label: 'Charts', render: () => <CrmDashboard auth={auth} isManager onNavigate={onNavigate} embedded hideTeam /> },
    leadManager
      ? { id: 'overview', label: 'Sales overview', render: () => <SalesOverview auth={auth} name={name} onNavigate={onNavigate} /> }
      : { id: 'desk', label: 'Lead desk', render: () => <LeadDesk auth={auth} name={name} onNavigate={onNavigate} self={self} /> },
    { id: 'recordings', label: 'Recordings', render: () => <CrmRecordings auth={auth} isManager embedded /> },
    { id: 'history', label: 'Move history', render: () => <CrmMoveHistory auth={auth} embedded /> },
  ]} />
);

// A Lead Manager (the leads the salespeople couldn't reach, and new leads
// while their switch is on) or a Sales Admin taking leads: their own calls
export const MyCalls: React.FC<HubProps & { name: string }> = ({ auth, tab, onTab, onNavigate, name }) => (
  <Hub title="My Calls" sub="Your own leads: the ones the salespeople couldn't reach after 3 tries each, and new leads while you take them" label="My calls" tab={tab} onTab={onTab} tabs={[
    { id: 'today', label: 'Today', render: () => <CrmToday auth={auth} name={name} embedded /> },
    { id: 'leads', label: 'My leads', render: () => <CrmMyLeads auth={auth} embedded /> },
    { id: 'followups', label: 'Follow-ups', render: () => <CrmFollowUps auth={auth} embedded /> },
    { id: 'week', label: 'My week', render: () => <MyWeek auth={auth} onNavigate={onNavigate} /> },
  ]} />
);

// A salesperson's week (level, rank, target) and their numbers
export const SalesProgress: React.FC<HubProps> = ({ auth, tab, onTab, onNavigate }) => (
  <Hub title="My Progress" sub="How your week is going" label="Progress" tab={tab} onTab={onTab} tabs={[
    { id: 'week', label: 'This week', render: () => <MyWeek auth={auth} onNavigate={onNavigate} /> },
    { id: 'numbers', label: 'My numbers', render: () => <CrmDashboard auth={auth} isManager={false} embedded /> },
  ]} />
);

// Student Coordinator meetings: a salesperson's own leads' (links to send);
// managers every meeting, and each coordinator's numbers (?tab=numbers)
export const MeetingsHub: React.FC<HubProps & { isManager: boolean }> = ({ auth, tab, onTab, isManager }) => {
  const { me } = useCrmMe(auth);
  const [open, setOpen] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  return (
    <>
      <CrmMeetings auth={auth} isManager={isManager} people={(me?.people || []).map(p => ({ id: p.id, name: p.name }))}
        onOpenLead={setOpen} tab={tab} onTab={onTab} refresh={refresh} />
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={() => setRefresh(n => n + 1)} />}
    </>
  );
};

// A salesperson: their shift (and asking for the other one), their
// attendance (and asking for a time), the holidays ahead, adding a lead of
// their own, and the calls they asked to fix
export const SalesRequests: React.FC<{ auth: CrmAuth }> = ({ auth }) => (
  <div className="space-y-4">
    <PageHead title="Shift & Requests" sub="Your shift this week and next, your attendance, the holidays ahead, leads of your own and calls to fix. A Lead Manager approves each request." />
    <CrmMyShift auth={auth} />
    <CrmMyAttendance auth={auth} />
    <HolidayList auth={auth} />
    <CrmRequestLead auth={auth} embedded />
    <CrmMyCallFixes auth={auth} />
  </div>
);
